// composables/useFieldDbInfo.ts
// Equivalente web ao LIST_F1 do Delphi (tecla F10 nos campos de cadastro).

import { ref } from "vue";
import type { FieldDbInfo } from "~/types/components/R3/fieldDbInfo.types";

const visible = ref(false);
const info = ref<FieldDbInfo | null>(null);

/**
 * Resolve o nome do formulário atual (rota) para o diálogo F10.
 */
function nomeFormularioAtual(): string {
	try {
		const route = useRoute();
		const nome = route.name != null ? String(route.name) : "";
		const path = route.path || "";
		return nome || path || "—";
	} catch {
		return "—";
	}
}

/**
 * Extrai metadados de banco a partir do elemento focado (data-db-*).
 * Percorre os ancestrais até encontrar `data-db-table` / `data-db-field`.
 */
export function extrairFieldDbInfoDoElemento(
	el: Element | null,
): FieldDbInfo | null {
	let node: Element | null = el;
	while (node && node !== document.body) {
		const tabela =
			(node as HTMLElement).dataset?.dbTable ||
			node.getAttribute("data-db-table");
		const campo =
			(node as HTMLElement).dataset?.dbField ||
			node.getAttribute("data-db-field");

		if (tabela || campo) {
			const observacao =
				(node as HTMLElement).dataset?.dbObs ||
				node.getAttribute("data-db-obs") ||
				undefined;
			const label =
				(node as HTMLElement).dataset?.dbLabel ||
				node.getAttribute("data-db-label") ||
				undefined;
			const controle =
				(node as HTMLElement).dataset?.dbControle ||
				node.getAttribute("data-db-controle") ||
				(node as HTMLElement).id ||
				node.getAttribute("name") ||
				undefined;

			let valorAtual: string | number | null = null;
			const focused = document.activeElement;
			if (focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement) {
				valorAtual = focused.value;
			}

			return {
				tabela: tabela?.trim() || "—",
				campo: campo?.trim() || "—",
				formulario: nomeFormularioAtual(),
				controle: controle || undefined,
				label: label || undefined,
				observacao: observacao || undefined,
				valorAtual,
			};
		}

		node = node.parentElement;
	}
	return null;
}

export const useFieldDbInfo = () => {
	/**
	 * Abre o diálogo LIST_F1 com os metadados informados.
	 */
	const showFieldDbInfo = (dados: FieldDbInfo) => {
		info.value = {
			formulario: dados.formulario || nomeFormularioAtual(),
			controle: dados.controle || "—",
			tabela: dados.tabela || "—",
			campo: dados.campo || "—",
			label: dados.label,
			observacao: dados.observacao,
			valorAtual: dados.valorAtual,
		};
		visible.value = true;
	};

	/**
	 * Tenta abrir a partir do elemento focado (ou do evento de teclado).
	 * @returns true se abriu o diálogo
	 */
	const showFieldDbInfoFromFocus = (el?: Element | null): boolean => {
		const alvo = el ?? document.activeElement;
		const dados = extrairFieldDbInfoDoElemento(alvo);
		if (!dados) return false;
		// Sem tabela e sem campo úteis → não abre
		if (
			(dados.tabela === "—" || !dados.tabela) &&
			(dados.campo === "—" || !dados.campo)
		) {
			return false;
		}
		showFieldDbInfo(dados);
		return true;
	};

	const hideFieldDbInfo = () => {
		visible.value = false;
	};

	return {
		visible,
		info,
		showFieldDbInfo,
		showFieldDbInfoFromFocus,
		hideFieldDbInfo,
		nomeFormularioAtual,
	};
};
